import { Icon } from '@components/ui';
import { useOutsideAlerter } from '@hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import styles from './CellValuePopover.module.scss';

export interface CellPopoverState {
  referenceElement: HTMLElement;
  title: string;
  content: React.ReactNode;
}

interface CellValuePopoverProps {
  state: CellPopoverState | null;
  onClose: () => void;
}

export const CellValuePopover = ({ state, onClose }: CellValuePopoverProps) => {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const setPopperRef = useCallback((node: HTMLDivElement | null) => {
    popperRef.current = node;
    setPopperElement(node);
  }, []);

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(state?.referenceElement ?? null, popperElement, {
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 6] } },
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  });

  useOutsideAlerter(popperRef, () => {
    if (state) onClose();
  });

  useEffect(() => {
    if (!state) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [state, onClose, update]);

  if (!state) return null;

  return createPortal(
    <div
      ref={setPopperRef}
      className={styles.cellPopover}
      style={popperStyles.popper}
      {...attributes.popper}
    >
      <div className={styles.header}>
        <span className={styles.title}>{state.title}</span>
        <Icon
          name="close"
          className={styles.closeButton}
          aria-label="Close"
          onClick={onClose}
        />
      </div>
      <div className={styles.content}>{state.content}</div>
    </div>,
    document.body,
  );
};
